import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { pickBy } from "lodash";
import styled from "styled-components";

import Checkbox from "@adsk/alloy-react-checkbox";
import { MultiControl } from "@adsk/alloy-react-dropdown";
import Footer, { ClearButton } from "@adsk/alloy-react-footer";
import { useLocale } from "@adsk/alloy-react-locale";
import Theme from "@adsk/alloy-react-theme";
import {
  DropdownTree,
  TreeNode,
  TreeSelectedIds,
  useDropdownTree,
} from "@adsk/alloy-react-tree";

import useAlloyReactTree, {
  TreeUpdate,
} from "../helpers/hooks/useAlloyReactTree";
import i18n from "../i18n";
import { CategoryDTO } from "../models";
import { convertToReact17CompatibleControlProps } from "../types";
import { ColumnFormField } from "./commonStyledComponents";

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: ${Theme.shadows.highElevation};
  background-color: ${Theme.colors.white};
  max-height: 300;
  max-width: calc(100% + 70px);
  border-radius: 4px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  > label {
    font-size: ${Theme.typography.labelSmall};
    padding-left: 8px;
  }
`;

type CategoryFilterProps = {
  includeSubcategories: boolean;
  categories: CategoryDTO[];
  selectedCategories?: CategoryDTO["id"][];
  onSelectCategory: (selectedIds: CategoryDTO["id"][]) => void;
  onSelectIncludeSubcategories: (isSelected: boolean) => void;
  onClearSelection: () => void;
};

const CategoryFilter: FC<CategoryFilterProps> = ({
  includeSubcategories,
  categories,
  selectedCategories = [],
  onSelectCategory,
  onSelectIncludeSubcategories,
  onClearSelection,
}) => {
  const { T } = useLocale(i18n);
  const { normalizeTreeData, getTreeSelectedIds } = useAlloyReactTree();
  const [categoryTreeSelectedIds, setCategoryTreeSelectedIds] =
    useState<TreeSelectedIds>({});

  const normalizedData = useMemo(() => {
    return normalizeTreeData(categories);
  }, [categories, normalizeTreeData]);

  const normalizedSelectedIds = useMemo(() => {
    return selectedCategories.reduce(
      (normalizedData, category) => ({ ...normalizedData, [category]: true }),
      {}
    );
  }, [selectedCategories]);

  const { controlProps, normalizedTree, getTreeNodeProps, treeProps, items } =
    useDropdownTree({
      denormalizedTree: normalizedData,
      getLabelFromNode: ({ label }) => label as string,
      initialExpandAll: true,
      selectedIds: categoryTreeSelectedIds,
      onSelect: (changes) => {
        const newTreeSelectedIds = getTreeSelectedIds(
          changes as TreeUpdate,
          normalizedSelectedIds!,
          !!includeSubcategories,
          normalizedTree
        );

        const categoryIds = Object.keys(
          pickBy(
            newTreeSelectedIds,
            (value) => value && value !== "indeterminate"
          )
        );

        setCategoryTreeSelectedIds(newTreeSelectedIds);
        onSelectCategory(categoryIds as string[]);
      },
    });

  const renderedItems = useMemo(
    () =>
      items.map((node) => {
        const treeNodeProps = getTreeNodeProps(node);

        return node.original.show ? (
          <TreeNode
            key={treeNodeProps.id}
            {...treeNodeProps}
            isMultiSelectable
          />
        ) : null;
      }),
    [getTreeNodeProps, items]
  );

  const onClickClearButton = useCallback(() => {
    setCategoryTreeSelectedIds({});
    onClearSelection();
  }, [onClearSelection]);

  // Clear CategoryTreeSelectedIds when filters were removed externally
  useEffect(() => {
    if (!selectedCategories.length) {
      setCategoryTreeSelectedIds({});
    }
  }, [selectedCategories.length]);

  return (
    <ColumnFormField label={T.translate("categoryFilter.label")}>
      <MultiControl
        // TODO: Remove this when we update react to version 18, see note in
        // src/types.ts for more info
        {...convertToReact17CompatibleControlProps(controlProps)}
        placeholder={T.translate("categoryFilter.placeholder") as string}
      />
      <MenuWrapper>
        <DropdownTree
          {...treeProps}
          renderFooter={(props) => (
            <Footer {...props} style={{}}>
              <CheckboxWrapper>
                <Checkbox
                  checked={includeSubcategories}
                  onChange={(isSelected) =>
                    onSelectIncludeSubcategories(isSelected as boolean)
                  }
                />
                <label htmlFor="includeSubcategoryCheckbox">
                  {T.translate("categoryFilter.includeSubcategories")}
                </label>
              </CheckboxWrapper>
              <ClearButton
                onClick={onClickClearButton}
                disabled={!selectedCategories.length}
              />
            </Footer>
          )}
        >
          {renderedItems}
        </DropdownTree>
      </MenuWrapper>
    </ColumnFormField>
  );
};

export default CategoryFilter;
